import * as React from "react";
import styled from "styled-components";

import { Text } from "src/library/components/Text";
import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import { IScheduleFormData } from "src/core/schedules/components/ScheduleForm/ScheduleEditForm";
import useCanSubmitStandingOrder from "src/core/lists/hooks/useCanSubmitStandingOrder";
import { ErrorNotice } from "src/core/shared/components/ErrorNotice";
import { IDrop } from "src/core/drops/api/drops.api";
import { ISchedule } from "src/core/schedules/api/schedules.api";
import {
  IError,
  IListErrorData
} from "src/core/errors/api/errors.api";
import { IOrderStatus } from "src/core/orders/api/orders.api";

import { IList } from "../../api/lists.api";
import ListScheduleWrapperButton from "./ListScheduleWrapperButton";
import ListScheduleInfo from "./ListScheduleInfo";

interface ListScheduleWrapperProps {
  // current list before changes
  list: IList;
  terminology: ITerminology;
  isLoading: boolean;
  isScheduleLoading: boolean;
  // scheduled form data that changes
  data: IList | null;
  schedule: ISchedule | null;
  drop: IDrop;
  handleSuspendSchedule: (id: number) => void;
  handleReactivateSchedule: (id: number) => void;
  handleSetSchedule: (form: IScheduleFormData) => void;
  scheduleErrors?: Array<IError<IListErrorData>>;
  orderStatuses: IOrderStatus[];
  scheduleViewSetting: "orders" | "schedule" | null;
  isEditingList: boolean;
}

const ListScheduleWrapper: React.ReactFCWithChildren<ListScheduleWrapperProps> = props => {
  const {
    list, terminology, schedule, isScheduleLoading = false, scheduleErrors, isLoading, isEditingList
  } = props;

  const [ showStandingOrderDetails, setShowStandingOrderDetails ] = React.useState(false);

  const handleToggleShowStandingOrderDetails = () => {
    setShowStandingOrderDetails(!showStandingOrderDetails);
  };

  React.useEffect(() => {
    if (schedule?.status === "active") {
      setShowStandingOrderDetails(true);
    }
  }, [ schedule?.status ]);

  const canSubmitStandingOrder = useCanSubmitStandingOrder();

  if (!list) {
    return null;
  }

  return isEditingList ? (
    <></>
  ) : (
    <Wrapper>
      <ListScheduleHeader
        onClick={() => (!isScheduleLoading && (schedule || showStandingOrderDetails) ? handleToggleShowStandingOrderDetails() : null)}
      >
        <Text
          fontSize="h2"
          textTransform="capitalize"
        >
          {terminology.schedule.singular.text}
        </Text>

        <ListScheduleWrapperButton
          showStandingOrderDetails={showStandingOrderDetails}
          setShowStandingOrderDetails={value => setShowStandingOrderDetails(value)}
          list={list}
          terminology={terminology}
          schedule={schedule}
          isScheduleLoading={isScheduleLoading}
          scheduleErrors={scheduleErrors}
          isLoading={isLoading}
        />
      </ListScheduleHeader>

      {showStandingOrderDetails && (
        <Group>
          <ErrorNotice
            type="error"
            message={canSubmitStandingOrder(list)}
          />

          <ListScheduleInfo {...props} />
        </Group>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.margins.md};
`;

const Group = styled.div``;

const ListScheduleHeader = styled.div`
  background-color: white;
  border-radius: ${({ theme }) => theme.borderRadius};
  padding: ${({ theme }) => theme.spacing.padding.md};
  margin-top: ${({ theme }) => theme.spacing.padding.md};
  height: 70px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .icon {
    position: relative;
    top: 1px;
    float: right;
  }
`;

export default ListScheduleWrapper;
